import React from 'react'
import {
	Badge,
	Button,
	Divider,
	FormControl,
	FormErrorMessage,
	FormHelperText,
	FormLabel,
	Grid,
	Input,
	Link,
	NumberInput,
	NumberInputField,
	Spinner,
	Stack,
	Text,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'

import { useAsaasConfigQuery } from '../queries/useAsaasConfigQuery'
import { useSaveAsaasConfigMutation } from '../queries/useSaveAsaasConfigMutation'

interface FormData {
	apiKey?: string
	interestPercentage?: number
	finePercentage?: number
	daysToDueDate?: number
}

function parseOptionalNumber(value?: number) {
	return Number.isNaN(value) ? undefined : value
}

export function PaymentMethodsRoute() {
	const query = useAsaasConfigQuery()
	const mutation = useSaveAsaasConfigMutation()
	const form = useForm<FormData>({
		defaultValues: {
			interestPercentage: 1,
			finePercentage: 2,
			daysToDueDate: 3,
		},
	})

	React.useEffect(() => {
		if (!query.data) return

		form.reset({
			interestPercentage:
				query.data.billingDefaults?.interestPercentage ?? 1,
			finePercentage: query.data.billingDefaults?.finePercentage ?? 2,
			daysToDueDate: query.data.billingDefaults?.daysToDueDate ?? 3,
		})
	}, [form, query.data])

	const onSubmit = form.handleSubmit((values) => {
		mutation.mutate({
			...values,
			apiKey: values.apiKey || undefined,
			interestPercentage: parseOptionalNumber(values.interestPercentage),
			finePercentage: parseOptionalNumber(values.finePercentage),
			daysToDueDate: parseOptionalNumber(values.daysToDueDate),
		})
	})

	if (query.isLoading) return <Spinner />

	return (
		<Stack
			as="form"
			onSubmit={onSubmit}
			gap="6"
			bg="white"
			_dark={{ bg: '#182533' }}
			p="6"
			rounded="12"
			maxW="760px"
		>
			<Stack direction="row" alignItems="center" gap="3">
				<Text fontWeight="700">Asaas</Text>
				<Badge colorScheme={query.data?.configured ? 'green' : 'red'}>
					{query.data?.configured ? 'Configurado' : 'Pendente'}
				</Badge>
				{query.data?.apiKeyPreview && (
					<Text fontSize="sm" color="gray.500">
						{query.data.apiKeyPreview}
					</Text>
				)}
			</Stack>

			<FormControl isRequired={!query.data?.configured} isInvalid={!!form.formState.errors.apiKey}>
				<FormLabel fontSize="md" fontWeight="700">
					Token Asaas
				</FormLabel>
				<Input
					{...form.register('apiKey', {
						required: query.data?.configured
							? false
							: 'Informe o token Asaas para ativar a integração',
					})}
					size="md"
					type="password"
					placeholder={
						query.data?.configured
							? 'Manter token atual'
							: 'Cole aqui o token Asaas'
					}
				/>
				<FormErrorMessage>
					{form.formState.errors.apiKey?.message}
				</FormErrorMessage>
				<FormHelperText>
					Único campo obrigatório. No painel da sua conta Asaas, vá em{' '}
					<Text as="span" fontWeight="600">
						Integrações → Chaves de API → Chaves de API
					</Text>
					, gere/copie o token e cole acima.{' '}
					<Link
						href="https://www.asaas.com/customerApiAccessToken/index"
						isExternal
						color="primary.500"
						fontWeight="600"
					>
						Abrir página de chaves do Asaas
					</Link>
					.
				</FormHelperText>
			</FormControl>

			<Divider />

			<Text fontSize="sm" fontWeight="700" color="gray.500">
				Configurações opcionais
			</Text>

			<Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="5">
				<FormControl>
					<FormLabel fontSize="sm" fontWeight="600">
						Dias para vencimento
					</FormLabel>
					<NumberInput size="sm" min={0}>
						<NumberInputField
							{...form.register('daysToDueDate', {
								valueAsNumber: true,
							})}
						/>
					</NumberInput>
					<FormHelperText>
						Prazo padrão do boleto. Usado quando o pagamento não tem
						vencimento ou já venceu (Asaas não aceita data passada).
					</FormHelperText>
				</FormControl>

				<FormControl>
					<FormLabel fontSize="sm" fontWeight="600">
						Juros ao mês (%)
					</FormLabel>
					<NumberInput size="sm" min={0} precision={2}>
						<NumberInputField
							{...form.register('interestPercentage', {
								valueAsNumber: true,
							})}
						/>
					</NumberInput>
				</FormControl>

				<FormControl>
					<FormLabel fontSize="sm" fontWeight="600">
						Multa (%)
					</FormLabel>
					<NumberInput size="sm" min={0} precision={2}>
						<NumberInputField
							{...form.register('finePercentage', {
								valueAsNumber: true,
							})}
						/>
					</NumberInput>
				</FormControl>
			</Grid>

			<Button
				type="submit"
				alignSelf="flex-end"
				size="sm"
				colorScheme="primary"
				textTransform="uppercase"
				isLoading={mutation.isPending}
			>
				Salvar
			</Button>
		</Stack>
	)
}
